import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, Input, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PreProcurementService, ProcurementNote } from 'src/app/services/pre-procurement.service';

@Component({
  selector: 'app-pre-procurement-notes',
  templateUrl: './pre-procurement-notes.component.html',
  styleUrls: ['./pre-procurement-notes.component.scss'],
  standalone: false,
})
export class PreProcurementNotesComponent {
  @Input() preProcurementId: string | number;
  @ViewChild('notesModal') notesModal!: TemplateRef<unknown>;
  @ViewChild('pdfViewerModal') pdfViewerModal!: TemplateRef<unknown>;

  procurementNotes: ProcurementNote[] = [];
  notesLoading = false;
  notesError = '';
  selectedPdfUrl: SafeResourceUrl | null = null;
  selectedAttachment: any = null;

  constructor(
    private preProcurementService: PreProcurementService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
  ) {}

  openNotes(preProcurementId: string | number = this.preProcurementId): void {
    if (preProcurementId === null || preProcurementId === undefined || preProcurementId === '') return;
    this.procurementNotes = [];
    this.notesError = '';
    this.notesLoading = true;
    this.modalService.open(this.notesModal, {
      size: 'xl',
      centered: true,
      ariaLabelledBy: 'procurement-notes-title',
    });
    this.preProcurementService.getPreProcurementNotes(preProcurementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (notes) => {
          this.procurementNotes = notes;
          this.notesLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.notesError = 'Unable to load procurement notes. Please try again.';
          this.notesLoading = false;
        },
      });
  }

  downloadAttachment(attachment: any): void {
    const attachmentUrl: string = attachment?.url;
    if (!attachmentUrl) return;

    const fileName = this.getDownloadFileName(attachment);
    if (attachmentUrl.startsWith('data:')) {
      try {
        const [header, data = ''] = attachmentUrl.split(',', 2);
        const mimeType = header.match(/^data:([^;]+)/i)?.[1] || 'application/pdf';
        const content = /;base64/i.test(header)
          ? Uint8Array.from(atob(data), (char) => char.charCodeAt(0))
          : decodeURIComponent(data);
        this.saveBlob(new Blob([content], { type: mimeType }), fileName);
      } catch (error) {
        console.error('Attachment download failed:', error);
      }
      return;
    }

    this.http.get(attachmentUrl, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => this.saveBlob(blob, fileName),
        error: (error) => console.error('Attachment download failed:', error),
      });
  }

  private getDownloadFileName(attachment: any): string {
    const name = String(attachment?.name || '').trim();
    if (name && /\.[a-z0-9]{2,5}$/i.test(name)) return name;

    const url = String(attachment?.url || '');
    const pathName = url.startsWith('data:') ? '' : decodeURIComponent(url.split(/[?#]/)[0].split('/').pop() || '');
    if (pathName && /\.[a-z0-9]{2,5}$/i.test(pathName)) return pathName;
    return `${name || 'attachment'}.pdf`;
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  viewAttachment(attachment: any): void {
    try {
      this.selectedAttachment = attachment;
      const [header, base64Data] = attachment.url.split(',');
      if (!header || !base64Data) return;
      const mimeType = header.match(/data:(.*?);base64/i)?.[1] || 'application/pdf';
      const bytes = atob(base64Data);
      const byteArray = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) byteArray[i] = bytes.charCodeAt(i);
      const blobUrl = URL.createObjectURL(new Blob([byteArray], { type: mimeType }));
      // Keep the browser's built-in PDF toolbar hidden so downloads use the
      // app's filename-aware download action in the preview header.
      this.selectedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${blobUrl}#toolbar=0&navpanes=0`,
      );
      this.modalService.open(this.pdfViewerModal, { size: 'xl', centered: true, scrollable: true });
    } catch (error) {
      console.error('Unable to view attachment:', error);
    }
  }
}